import type { CSSProperties, FocusEvent, RefObject } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';

import type { Classnames, InputSize, Maybe } from '@snowit/icetypes';
import type { InputOnKeyDown } from '@snowit/icetypes/events';

import { Unicodes } from '@snowit/iceconst';
import useLanguage from '@snowit/system/useLanguage';
import { Input } from '@snowit/ui';
import snowment from '@snowit/utils/snowmentv2';

import ButtonIcon from '../../Buttons/ButtonIcon';
import Typo from '../../Typography/Typo';

import cx from 'classnames';
import s from './InputDateV3.module.scss';

export type InputDateV3Props = {
  size?: InputSize;
  label?: string;
  hint?: string;
  error?: boolean;
  errorMsg?: string;
  value?: Maybe<string>;
  onChange?: (value: string | undefined, valid: boolean) => void;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  classNames?: Classnames<'wrapper'>;
};

type DateSegment = 'day' | 'month' | 'year';

const pad = (val: string) => val.padStart(2, '0');

const parseValue = (value?: Maybe<string>): Record<DateSegment, string> => {
  if (!value) return { day: '', month: '', year: '' };
  const [y, m, d] = value.split('-');
  return { day: d ?? '', month: m ?? '', year: y ?? '' };
};

const clamp = (val: string, min: number, max: number) => {
  const n = parseInt(val, 10);
  if (!val || Number.isNaN(n)) return val;
  return String(Math.min(Math.max(n, min), max));
};

const InputDateV3 = (props: InputDateV3Props) => {
  const {
    size = 'tr',
    label,
    hint,
    error,
    errorMsg,
    value,
    onChange,
    disabled,
    readOnly,
    required,
    classNames,
  } = props;
  const { t } = useLanguage();
  const [parsed, setParsed] = useState<Record<DateSegment, string>>(parseValue(value));

  const hasError = error;

  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  // sync external value changes
  useEffect(() => {
    setParsed(parseValue(value));
  }, [value]);

  const handleEmit = (next: Record<DateSegment, string>) => {
    if (typeof onChange !== 'function') return;
    if (!next.year && !next.month && !next.day) {
      onChange(undefined, false);
      return;
    }
    const date = [next.year, next.month, next.day].join('-');
    const dateObj = snowment(date);
    onChange(date, dateObj.valid);
  };

  const handleReset = () => {
    if (typeof onChange !== 'function') return;
    onChange(undefined, false);
  };

  const handleChange = (
    type: DateSegment,
    raw: string,
    next?: RefObject<HTMLInputElement | null>,
  ) => {
    const sliceNum = type === 'year' ? 4 : 2;
    const digits = raw.replace(/\D/g, '').slice(0, sliceNum);
    let adjusted: string;
    switch (type) {
      case 'day':
        adjusted = digits.length === 2 ? pad(clamp(digits, 1, 31)) : digits;
        break;
      case 'month':
        adjusted = digits.length === 2 ? pad(clamp(digits, 1, 12)) : digits;
        break;
      default:
        adjusted = digits;
    }
    setParsed((prev) => {
      const next = { ...prev, [type]: adjusted };
      handleEmit(next);
      return next;
    });

    if (type !== 'year' && adjusted.length === 2 && next?.current) {
      next.current.focus();
    }
  };

  const handleFocus = (ref?: RefObject<HTMLInputElement | null>) => {
    const el = ref?.current;
    if (!el) return;
    setTimeout(() => el.select(), 0);
  };

  const handleBlur = (type: DateSegment, event: FocusEvent<HTMLInputElement>) => {
    const current = event.target.value;
    const adjusted = current ? pad(clamp(current, 1, type === 'day' ? 31 : 12)) : '';
    if (adjusted !== current) {
      setParsed((prev) => {
        const next = { ...prev, [type]: adjusted };
        handleEmit(next);
        return next;
      });
    }
  };

  const makeKeyHandler = (current: string, prevRef?: RefObject<HTMLInputElement | null>) => {
    return (handler: InputOnKeyDown) => {
      const { key, event } = handler;
      if (key === 'Backspace' && current === '' && prevRef) {
        event.preventDefault();
        prevRef.current?.focus();
      } else if (key.length === 1 && !/\d/.test(key)) {
        event.preventDefault();
      }
    };
  };

  const renderLabel = () => {
    if (!label) return null;
    if (size !== 'tr') return null;
    return <Typo className={s.label} text={label} tag={'label'} size={'sm'} />;
  };

  const renderInputs = () => {
    return (
      <Fragment>
        <Input
          ref={dayRef}
          classNames={{ wrapper: s.day, input: s.input }}
          placeholder={t(`lzy.time.dd`)}
          value={parsed.day}
          onChange={({ value }) => handleChange('day', value, monthRef)}
          onKeyDown={makeKeyHandler(parsed.day)}
          onFocus={() => handleFocus(dayRef)}
          onBlur={({ event }) => handleBlur('day', event)}
        />
        <Typo text={Unicodes.SLASH} color={'text-l'} />
        <Input
          ref={monthRef}
          classNames={{ wrapper: s.month, input: s.input }}
          placeholder={t(`lzy.time.mm`)}
          value={parsed.month}
          onChange={({ value }) => handleChange('month', value, yearRef)}
          onKeyDown={makeKeyHandler(parsed.month, dayRef)}
          onFocus={() => handleFocus(monthRef)}
          onBlur={({ event }) => handleBlur('month', event)}
        />
        <Typo text={Unicodes.SLASH} color={'text-l'} />
        <Input
          ref={yearRef}
          classNames={{ wrapper: s.year, input: s.input }}
          placeholder={t(`lzy.time.yyyy`)}
          value={parsed.year}
          onChange={({ value }) => handleChange('year', value)}
          onKeyDown={makeKeyHandler(parsed.year, monthRef)}
          onFocus={() => handleFocus(yearRef)}
        />
      </Fragment>
    );
  };

  const renderClearButton = () => {
    if (disabled || !value) return null;
    // if (!day && !month && !year) return null;
    return (
      <ButtonIcon
        classNames={{ button: s.clear }}
        icon={'close'}
        onClick={handleReset}
        theme={'transparent'}
        size={size === 'db' ? 'xxl' : 'db'}
        color={'text'}
        rounded
      />
    );
  };

  const renderHint = () => {
    // if (hasError && errorMsg) return null;
    if (!hint) return null;
    return (
      <Typo
        className={s.hint}
        text={hint}
        tag={'p'}
        size={'md'}
        color={hasError ? 'error' : 'text-l'}
      />
    );
  };

  const renderError = () => {
    if (!hasError) return null;
    if (!errorMsg) return null;
    if (hasError && !errorMsg) return null;

    return <Typo className={s.hint} text={errorMsg} tag={'p'} size={'md'} color={'error'} />;
  };

  return (
    <div className={cx(s.wrapper, classNames)}>
      <div
        className={cx(s.wrapInput, {
          [s.disabled]: disabled,
          [s.error]: hasError,
          [s.readOnly]: readOnly,
          [s.required]: required,
        })}
        style={{ '--l-h': `var(--s-${size})` } as CSSProperties}
      >
        {renderInputs()}
        {renderLabel()}
        {renderClearButton()}
      </div>
      {renderHint()}
      {renderError()}
    </div>
  );
};

export default InputDateV3;
